/**
 * useMentions Hook
 *
 * Wires the pure `mentionsParser` utilities to a controlled text input.
 * Mirrors Flutter's _handleMentions / _clearMention / _selectMention state
 * machine in comments_bottom_sheet.dart.
 *
 * Usage:
 *   const input = useMentions({ value, onChange, selectUser: async () => await SearchUsersSheet.showForMention() });
 *   <input ref={input.inputRef} value={value} onChange={e => input.handleChange(e.target.value)} onSelect={...} />
 *
 * The hook opens an async `selectUser` picker when an in-progress @mention is
 * detected; if the picker returns a username, it is inserted; if it returns
 * null, the leading '@' is stripped.
 */

import { useCallback, useRef, useState } from 'react';
import {
  parseMentionAtCursor,
  insertMention,
  removeMention,
  removeMentionSymbol,
  type MentionAtCursor,
} from '@/domain/utils/mentionsParser';

export interface UseMentionsOptions {
  /**
   * Called when the user starts typing an @mention. Should return the chosen
   * username (without '@'), or null if the user cancelled.
   */
  selectUser: () => Promise<string | null>;
}

export interface UseMentionsResult {
  inputRef: React.MutableRefObject<HTMLInputElement | HTMLTextAreaElement | null>;
  /**
   * True while the mention picker is awaiting a user selection. Consumers
   * should disable the text input to prevent concurrent edits racing the
   * async resolution (stale `previousTextRef` / `activeMentionRef`).
   */
  isEditingMentions: boolean;
  /**
   * Call from input onChange. Pass the new value and the new cursor position.
   * Returns `true` when the hook has taken over the write (via `setValue`) and
   * the caller should skip its own default `setValue(newValue)`; returns
   * `false` when the caller should apply `newValue` as usual.
   */
  handleChange: (
    newValue: string,
    cursorPos: number,
    setValue: (text: string, cursorPos: number) => void,
  ) => boolean;
}

export function useMentions({ selectUser }: UseMentionsOptions): UseMentionsResult {
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  // Mirrors Flutter state
  const [isEditingMentions, setIsEditingMentions] = useState(false);
  const activeMentionRef = useRef<MentionAtCursor | null>(null);
  const previousTextRef = useRef<string>('');

  const resetMentionState = useCallback(() => {
    setIsEditingMentions(false);
    activeMentionRef.current = null;
  }, []);

  const handleChange = useCallback<UseMentionsResult['handleChange']>(
    (newValue, cursorPos, setValue) => {
      const prev = previousTextRef.current;
      const isDeleting = newValue.length < prev.length;

      const atCursor = parseMentionAtCursor(newValue, cursorPos);

      if (!atCursor) {
        resetMentionState();
        previousTextRef.current = newValue;
        return false;
      }

      // Any character removed from an @-mention wipes the whole mention.
      if (isDeleting) {
        const { text, cursorPos: nextCursor } = removeMention(
          newValue,
          atCursor.startPos,
          atCursor.endPos,
        );
        previousTextRef.current = text;
        resetMentionState();
        setValue(text, nextCursor);
        return true;
      }

      // Suppress mid-mention edits (user typed inside an existing resolved mention)
      if (atCursor.mention.length > 0 && cursorPos !== atCursor.startPos) {
        setValue(prev, atCursor.endPos);
        previousTextRef.current = prev;
        return true;
      }

      setIsEditingMentions(true);
      activeMentionRef.current = atCursor;
      previousTextRef.current = newValue;

      // Fire-and-forget: the picker will call setValue when the user resolves it.
      // We return false so the caller still writes `newValue` now (the input
      // needs to reflect the "@" the user just typed until the picker resolves).
      void (async () => {
        try {
          const username = await selectUser();
          const active = activeMentionRef.current;
          if (!active) return;
          if (username) {
            const { text, cursorPos: nextCursor } = insertMention(
              newValue,
              active.startPos,
              active.endPos,
              username,
            );
            previousTextRef.current = text;
            resetMentionState();
            setValue(text, nextCursor);
          } else {
            const { text, cursorPos: nextCursor } = removeMentionSymbol(
              newValue,
              active.endPos,
            );
            previousTextRef.current = text;
            resetMentionState();
            setValue(text, nextCursor);
          }
        } catch {
          resetMentionState();
        }
      })();

      return false;
    },
    [resetMentionState, selectUser],
  );

  return { inputRef, isEditingMentions, handleChange };
}
